import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { motion, useInView } from 'framer-motion';
import SharedFeatureCard from '@/components/FeatureCard';
import {
  Waves, Droplets, Gauge, Beaker, FlaskConical, Cog,
  Zap, ArrowRight, ShieldCheck, TrendingUp, Microscope,
  Database, Wind, Factory, Thermometer, Activity, CheckCircle2,
  AlertTriangle, ChevronRight, Sparkles, Atom,
  BarChart3, Timer, Recycle,
  MapPin, Mail, Filter, FileText, Users, Globe, Cpu,
  ClipboardList, ScanSearch, LayoutDashboard, BookOpen,
  Layers, FolderOpen, LineChart,
  Workflow, Mountain, Volume2, GitBranch, Box, AlertOctagon,
  PoundSterling, Calculator, Upload, Wrench, Leaf, Grid3X3,
  ClipboardCheck, ShieldAlert, Boxes,
  KeyRound, List, TestTube, SlidersHorizontal,
} from 'lucide-react';

/* ═══════════════════════════════════════════════════════════
   REYNOLDS & BAUHM — INTERACTIVE ADVERTISEMENT PAGE
   Enterprise-grade showcase with live physics animations
   ═══════════════════════════════════════════════════════════ */

/* ─── Animated Counter ─────────────────────────────────── */
function AnimatedCounter({ target, suffix = '', prefix = '', duration = 2 }: {
  target: number; suffix?: string; prefix?: string; duration?: number;
}) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });

  useEffect(() => {
    if (!isInView) return;
    let start = 0;
    const steps = 60;
    const increment = target / steps;
    const interval = setInterval(() => {
      start += increment;
      if (start >= target) {
        setCount(target);
        clearInterval(interval);
      } else {
        setCount(Math.floor(start));
      }
    }, (duration * 1000) / steps);
    return () => clearInterval(interval);
  }, [isInView, target, duration]);

  return <span ref={ref}>{prefix}{count.toLocaleString()}{suffix}</span>;
}

/* ─── DAF Bubble Canvas ────────────────────────────────── */
interface Bubble {
  x: number; y: number; r: number; speed: number; wobble: number;
  opacity: number; phase: number;
}

function DAFBubbleCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const bubblesRef = useRef<Bubble[]>([]);
  const frameRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      canvas.width = canvas.offsetWidth * window.devicePixelRatio;
      canvas.height = canvas.offsetHeight * window.devicePixelRatio;
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    };
    resize();
    window.addEventListener('resize', resize);

    // Init bubbles
    const W = canvas.offsetWidth;
    const H = canvas.offsetHeight;
    const bubbles: Bubble[] = [];
    for (let i = 0; i < 80; i++) {
      bubbles.push({
        x: Math.random() * W,
        y: H + Math.random() * H,
        r: 1 + Math.random() * 3.5,
        speed: 0.3 + Math.random() * 1.2,
        wobble: 0.5 + Math.random() * 1.5,
        opacity: 0.15 + Math.random() * 0.35,
        phase: Math.random() * Math.PI * 2,
      });
    }
    bubblesRef.current = bubbles;

    const animate = (time: number) => {
      const w = canvas.offsetWidth;
      const h = canvas.offsetHeight;
      ctx.clearRect(0, 0, w, h);

      // Draw tank gradient
      const grad = ctx.createLinearGradient(0, 0, 0, h);
      grad.addColorStop(0, 'rgba(10,22,40,0.3)');
      grad.addColorStop(0.5, 'rgba(15,31,58,0.15)');
      grad.addColorStop(1, 'rgba(10,22,40,0.4)');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, w, h);

      // Draw water surface line
      ctx.strokeStyle = 'rgba(56,189,248,0.2)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      for (let x = 0; x < w; x += 2) {
        const y = 40 + Math.sin(x * 0.02 + time * 0.001) * 3;
        if (x === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.stroke();

      // Draw bubbles
      bubblesRef.current.forEach((b) => {
        b.y -= b.speed;
        const wobbleX = b.x + Math.sin(time * 0.002 + b.phase) * b.wobble * 10;

        // Bubble body
        ctx.beginPath();
        ctx.arc(wobbleX, b.y, b.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(200,230,255,${b.opacity})`;
        ctx.fill();

        // Highlight
        ctx.beginPath();
        ctx.arc(wobbleX - b.r * 0.3, b.y - b.r * 0.3, b.r * 0.25, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${b.opacity * 1.5})`;
        ctx.fill();

        // Reset when off top
        if (b.y < -10) {
          b.y = h + 10;
          b.x = Math.random() * w;
        }
      });

      // Draw floc particles (rising slower, larger)
      for (let i = 0; i < 20; i++) {
        const fx = (w * 0.3 + i * 25 + Math.sin(time * 0.001 + i) * 30) % w;
        const fy = (h - ((time * 0.05 + i * 80) % (h + 100))) + 50;
        const fr = 2 + Math.sin(i) * 1;
        ctx.beginPath();
        ctx.arc(fx, fy, fr, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(205,164,46,${0.08 + Math.sin(time * 0.003 + i) * 0.04})`;
        ctx.fill();
      }

      frameRef.current = requestAnimationFrame(animate);
    };

    frameRef.current = requestAnimationFrame(animate);
    return () => {
      cancelAnimationFrame(frameRef.current);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', zIndex: 0, pointerEvents: 'none' }}
    />
  );
}

/* ─── Scroll Reveal Wrapper ────────────────────────────── */
function Reveal({ children, delay = 0, className = '' }: {
  children: React.ReactNode; delay?: number; className?: string;
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: 'easeOut' }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ─── Feature Card ─────────────────────────────────────── */
/**
 * Thin wrapper around the shared FeatureCard component that adds the
 * scroll-reveal animation. Visual styling is owned by the shared component
 * so every page uses the same enterprise look.
 */
function FeatureCard({ icon: Icon, title, desc, color = 'var(--rb-brand)', delay = 0, onClick }: {
  icon: React.ElementType; title: string; desc: string; color?: string; delay?: number; onClick?: () => void;
}) {
  return (
    <Reveal delay={delay}>
      <SharedFeatureCard
        icon={Icon as React.ComponentType<{ className?: string; style?: React.CSSProperties }>}
        title={title}
        desc={desc}
        color={color}
        onClick={onClick}
      />
    </Reveal>
  );
}

/* ─── Equation Card ────────────────────────────────────── */
function EquationCard({ name, equation, desc, delay = 0 }: {
  name: string; equation: string; desc: string; delay?: number;
}) {
  return (
    <Reveal delay={delay}>
      <div className="p-5 hover:border-sky-500/20 transition-all rb-card-sm">
        <div className="flex items-center gap-2 mb-3">
          <Atom className="w-4 h-4 text-sky-400" />
          <span className="text-xs font-bold text-sky-400 uppercase tracking-wider">{name}</span>
        </div>
        <div className="font-mono text-sm text-amber-400 mb-2 bg-slate-950/60 rounded px-3 py-2 border border-slate-800">
          {equation}
        </div>
        <p className="text-xs text-slate-500">{desc}</p>
      </div>
    </Reveal>
  );
}

/* ─── Equipment Card ───────────────────────────────────── */
function EquipmentCard({ icon: Icon, name, desc, delay = 0 }: {
  icon: React.ElementType; name: string; desc: string; delay?: number;
}) {
  return (
    <Reveal delay={delay}>
      <div className="bg-slate-800/30 border border-slate-700/20 rounded-lg p-5 text-center hover:border-amber-500/20 hover:bg-slate-800/50 transition-all group">
        <div className="text-3xl mb-3 flex justify-center">
          <Icon className="w-10 h-10 text-slate-500 group-hover:text-amber-400 transition-colors" />
        </div>
        <h4 className="text-sm font-bold text-white mb-1">{name}</h4>
        <p className="text-xs text-slate-500">{desc}</p>
      </div>
    </Reveal>
  );
}

/* ─── Process Step ─────────────────────────────────────── */
function ProcessStep({ label, icon: Icon, delay = 0 }: {
  label: string; icon: React.ElementType; delay?: number;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      transition={{ delay, duration: 0.5 }}
      className="flex items-center gap-2"
    >
      <div className="flex flex-col items-center gap-1 bg-gradient-to-b from-slate-800 to-slate-900 border border-amber-500/15 rounded-lg px-4 py-3 min-w-[100px]">
        <Icon className="w-5 h-5 text-amber-400" />
        <span className="text-[10px] font-bold text-white whitespace-nowrap">{label}</span>
      </div>
      <ChevronRight className="w-4 h-4 text-amber-500/40 hidden md:block" />
    </motion.div>
  );
}

/* ─── Badge ────────────────────────────────────────────── */
function StatusBadge({ type, label }: { type: 'pass' | 'warn' | 'fail'; label: string }) {
  const styles = {
    pass: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    warn: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
    fail: 'bg-rose-500/10 text-rose-400 border-rose-500/20',
  };
  const icons = { pass: CheckCircle2, warn: AlertTriangle, fail: AlertTriangle };
  const Icon = icons[type];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${styles[type]}`}>
      <Icon className="w-3 h-3" /> {label}
    </span>
  );
}

/* ═══════════════════════════════════════════════════════════
   MAIN ADVERT PAGE
   ═══════════════════════════════════════════════════════════ */
export default function AdvertPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#020617] text-slate-200 overflow-x-hidden">
      {/* ═════ HERO ═════ */}
      <section className="relative min-h-screen flex flex-col justify-center items-center text-center px-6 py-20 overflow-hidden">
        <DAFBubbleCanvas />

        {/* Radial glows */}
        <div className="absolute inset-0 pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse at 20% 50%, rgba(30,58,95,0.3) 0%, transparent 50%), radial-gradient(ellipse at 80% 20%, rgba(205,164,46,0.06) 0%, transparent 40%)'
          }}
        />

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="relative z-10 max-w-4xl mx-auto"
        >
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-amber-500/30 text-amber-400 text-xs font-bold tracking-widest uppercase mb-8">
            <span className="w-2 h-2 rounded-full bg-emerald-400 shadow-[0_0_12px_#34d399] animate-pulse" />
            Live Physics Engine — Reynolds Bauhm Smart Tool v2.0
          </div>

          {/* Headline */}
          <h1 className="text-4xl sm:text-5xl md:text-7xl font-extrabold leading-[1.05] tracking-tight mb-6">
            <span className="bg-gradient-to-r from-white via-amber-200 to-white bg-[length:200%] bg-clip-text text-transparent animate-[shimmer_4s_linear_infinite]">
              Wastewater Design,
            </span>
            <br />
            <span className="text-white">Powered by </span>
            <span className="text-amber-400">Real Science</span>
          </h1>

          <p className="text-lg md:text-xl text-slate-400 font-light max-w-2xl mx-auto mb-10 leading-relaxed">
            A web-based platform that simulates dissolved air flotation,
            biological treatment, and chemical dosing with live physics —
            Henry's law, Stokes' settling, affinity laws, and van't Hoff equations
            running directly in the browser.
          </p>

          {/* CTAs */}
          <div className="flex flex-wrap gap-4 justify-center">
            <button
              onClick={() => navigate('/parameters')}
              className="group px-8 py-3.5 rounded-xl font-bold text-sm bg-gradient-to-r from-amber-500 to-amber-600 text-slate-200 shadow-lg shadow-amber-500/20 hover:shadow-amber-500/40 hover:-translate-y-0.5 transition-all flex items-center gap-2"
            >
              <Sparkles className="w-4 h-4" />
              Try the Calculator
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              onClick={() => navigate('/daf-spec')}
              className="px-8 py-3.5 rounded-xl font-bold text-sm border border-slate-600 text-slate-300 hover:border-amber-500/40 hover:text-amber-400 transition-all"
            >
              Explore DAF Physics
            </button>
          </div>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
        >
          <div className="w-6 h-10 rounded-full border-2 border-slate-600 flex justify-center pt-2">
            <div className="w-1 h-2 bg-amber-400 rounded-full" />
          </div>
        </motion.div>
      </section>

      {/* ═════ LIVE STATS ═════ */}
      <section className="py-20 px-6 border-t border-slate-800/50"
        style={{ background: 'linear-gradient(180deg, #0f1f3a, #020617)' }}>
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 text-center">
            <Reveal>
              <div className="stat-item">
                <h3 className="text-3xl md:text-4xl font-extrabold text-amber-400 leading-none">
                  <AnimatedCounter target={100} suffix="+" />
                </h3>
                <p className="text-xs text-slate-400 mt-2">Calculation Pages</p>
              </div>
            </Reveal>
            <Reveal delay={0.05}>
              <div className="stat-item">
                <h3 className="text-3xl md:text-4xl font-extrabold text-sky-400 leading-none">
                  <AnimatedCounter target={52} suffix="+" />
                </h3>
                <p className="text-xs text-slate-400 mt-2">Physics Equations</p>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="stat-item">
                <h3 className="text-3xl md:text-4xl font-extrabold text-emerald-400 leading-none">
                  <AnimatedCounter target={16} />
                </h3>
                <p className="text-xs text-slate-400 mt-2">Equipment Specs</p>
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="stat-item">
                <h3 className="text-3xl md:text-4xl font-extrabold text-violet-400 leading-none">
                  <AnimatedCounter target={43} />
                </h3>
                <p className="text-xs text-slate-400 mt-2">Pump Models</p>
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="stat-item">
                <h3 className="text-3xl md:text-4xl font-extrabold text-cyan-400 leading-none">
                  <AnimatedCounter target={10} />
                </h3>
                <p className="text-xs text-slate-400 mt-2">Simulation Modules</p>
              </div>
            </Reveal>
            <Reveal delay={0.25}>
              <div className="stat-item">
                <h3 className="text-3xl md:text-4xl font-extrabold text-rose-400 leading-none">
                  <AnimatedCounter target={4} />
                </h3>
                <p className="text-xs text-slate-400 mt-2">Global Standards</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ═════ SCIENCE ENGINE ═════ */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <div className="text-center mb-16">
              <div className="w-16 h-1 bg-amber-500 rounded-full mx-auto mb-6" />
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Science Engine Under the Hood
              </h2>
              <p className="text-slate-400 max-w-2xl mx-auto">
                Every calculation is derived from peer-reviewed physics and standard engineering references.
                All equations are implemented from primary sources with no empirical shortcuts.
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <EquationCard
              name="Henry's Law"
              equation="C = k_H · P · exp[-1300(1/T - 1/293)]"
              desc="Air solubility in water corrected for temperature and saturator pressure."
              delay={0}
            />
            <EquationCard
              name="Stokes' Law"
              equation="v = (2/9) · g · r² · (ρ_p - ρ_f) / μ"
              desc="Bubble rise velocity for DAF floatation — particle size, density, viscosity."
              delay={0.1}
            />
            <EquationCard
              name="Affinity Laws"
              equation="Q₂/Q₁ = N₂/N₁,  H₂/H₁ = (N₂/N₁)²"
              desc="Pump curve interpolation for any speed or impeller diameter."
              delay={0.2}
            />
            <EquationCard
              name="Power Number"
              equation="P = N_p · ρ · N³ · D⁵"
              desc="Flocculation mixer power from RPM, impeller diameter, and fluid density."
              delay={0.3}
            />
            <EquationCard
              name="van't Hoff"
              equation="ln(k₂/k₁) = (E_a/R)(1/T₁ - 1/T₂)"
              desc="Temperature correction for reaction rates and solubility."
              delay={0.4}
            />
            <EquationCard
              name="Nernst-Planck"
              equation="J = -D∇C - (zFD/RT)C∇φ"
              desc="Ion transport and membrane flux for advanced treatment design."
              delay={0.5}
            />
          </div>
        </div>
      </section>

      {/* ═════ INTERACTIVE DAF DEMO ═════ */}
      <section className="py-24 px-6 relative overflow-hidden"
        style={{ background: 'linear-gradient(180deg, #020617, #0a1628)' }}>
        <div className="absolute inset-0 pointer-events-none opacity-30">
          <DAFBubbleCanvas />
        </div>
        <div className="max-w-6xl mx-auto relative z-10">
          <Reveal>
            <div className="text-center mb-12">
              <div className="w-16 h-1 bg-sky-500 rounded-full mx-auto mb-6" />
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                See the Bubbles in Real Time
              </h2>
              <p className="text-slate-400 max-w-2xl mx-auto">
                The DAF module calculates air demand, bubble size distribution,
                rise velocity, and A/S ratio from Henry's law and Stokes' equation.
              </p>
            </div>
          </Reveal>

          <Reveal>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              {/* Tank visual */}
              <div className="relative h-80 md:h-96 rounded-2xl border border-slate-800/40 overflow-hidden bg-gradient-to-b from-slate-900 to-slate-950">
                <DAFBubbleCanvas />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900/80 border border-sky-500/20 mb-3">
                      <Activity className="w-4 h-4 text-sky-400 animate-pulse" />
                      <span className="text-xs font-bold text-sky-400">Live Simulation</span>
                    </div>
                    <div className="text-xs text-slate-500 font-mono">
                      Bubble size: 30–80 μm<br />
                      Rise velocity: 12–45 m/h<br />
                      A/S ratio: 0.025
                    </div>
                  </div>
                </div>
              </div>

              {/* Specs */}
              <div className="space-y-4">
                {[
                  { label: 'Air Solubility @ 20°C', value: '25 mg/L', bar: 65, color: '#38bdf8' },
                  { label: 'Saturator Efficiency', value: '85%', bar: 85, color: '#34d399' },
                  { label: 'Bubble Count', value: '2.4 × 10⁹ / m³', bar: 92, color: 'var(--rb-brand)' },
                  { label: 'Float Rise Rate', value: '18.5 m/h', bar: 72, color: '#f472b6' },
                ].map((s, i) => (
                  <Reveal key={i} delay={i * 0.1}>
                    <div className="bg-slate-800/30 border border-slate-700/20 rounded-lg p-4">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm text-slate-400">{s.label}</span>
                        <span className="text-sm font-bold" style={{ color: s.color }}>{s.value}</span>
                      </div>
                      <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          whileInView={{ width: `${s.bar}%` }}
                          viewport={{ once: true }}
                          transition={{ duration: 1.2, delay: 0.3 }}
                          className="h-full rounded-full"
                          style={{ background: s.color }}
                        />
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═════ FEATURES ═════ */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <div className="text-center mb-16">
              <div className="w-16 h-1 bg-emerald-500 rounded-full mx-auto mb-6" />
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Calculation Modules &amp; Platform Tools
              </h2>
              <p className="text-slate-400 max-w-2xl mx-auto">
                Covering the full workflow from influent characterisation
                through treatment design to engineering proposal.
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            <FeatureCard
              icon={Microscope}
              title="Influent Intelligence"
              desc="Enter 25+ water quality parameters. AI-powered source classification, treatability index, BOD/COD ratio checks, and live toxic parameter flags."
              color="#38bdf8"
              delay={0}
            />
            <FeatureCard
              icon={Waves}
              title="DAF Physics Simulator"
              desc="Full Henry's law + van't Hoff air system. Live bubble dynamics, recycle pump sizing, saturator pressure, A/S ratio, and flocculation G-value."
              color="var(--rb-brand)"
              delay={0.1}
            />
            <FeatureCard
              icon={Cog}
              title="Pump Selection Engine"
              desc="43 real pump models from EDUR, Nikuni, Performance Pro, QED. Interpolated H-Q/P-Q curves with live operating point overlay and affinity law scaling."
              color="#a78bfa"
              delay={0.2}
            />
            <FeatureCard
              icon={Beaker}
              title="Chemical Dosing Optimiser"
              desc="Auto-recommends coagulant type and dose based on TSS, O&G, pH, and alkalinity. Live pH-drop simulation with neutralisation buffer calculation."
              color="#f472b6"
              delay={0.3}
            />
            <FeatureCard
              icon={Layers}
              title="Process Train Builder"
              desc="Auto-assembles the optimal treatment sequence from influent data: Screening → EQ → DAF → Biological → Clarifier → Filtration → Disinfection."
              color="#34d399"
              delay={0.4}
            />
            <FeatureCard
              icon={ShieldCheck}
              title="Equipment Validation"
              desc="Every spec validated against OEM data and industry standards. HLR, SLR, A/S ratio, retention time, G-value — Pass / Warn / Fail with margin %."
              color="#f87171"
              delay={0.5}
            />
            <FeatureCard
              icon={ScanSearch}
              title="Influent Intelligence Panel"
              desc="Real-time source classification (dairy, municipal, industrial, abattoir…) with probability bars, salinity alarms, and coagulant advice."
              color="#818cf8"
              delay={0.6}
            />
            <FeatureCard
              icon={Database}
              title="Sample & Site Database"
              desc="Save influent samples with full 25+ parameter sets. Link to customers and sites. Full audit trail with report numbers and notes."
              color="#22d3ee"
              delay={0.7}
            />
            <FeatureCard
              icon={LineChart}
              title="Trend Analytics"
              desc="Visualise parameter trends across samples. Spot seasonal variation, process upsets, and consent exceedances with interactive charts."
              color="#fb923c"
              delay={0.8}
            />
            <FeatureCard
              icon={Recycle}
              title="Effluent Compliance"
              desc="Compare effluent against UK EA, AU EPA, EU UWWTD, or US EPA discharge standards. Instant pass/fail with margin calculations per parameter."
              color="#10b981"
              delay={0.9}
            />
            <FeatureCard
              icon={FileText}
              title="Enterprise Proposals"
              desc="Generate country-traceable proposal documents with equipment specs, sizing summaries, and process descriptions — ready for client presentation."
              color="#f59e0b"
              delay={1.0}
            />
            <FeatureCard
              icon={Users}
              title="Customer Management"
              desc="Multi-customer CRM: manage sites, permits, discharge routes, and sample history per customer. Searchable across all sites and samples."
              color="#06b6d4"
              delay={1.1}
            />
          </div>
        </div>
      </section>

      {/* ═════ COMPLIANCE STANDARDS ═════ */}
      <section className="py-20 px-6 border-t border-slate-800/30"
        style={{ background: 'linear-gradient(180deg, #020617, #0a1628)' }}>
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <div className="text-center mb-12">
              <div className="w-16 h-1 bg-violet-500 rounded-full mx-auto mb-6" />
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Global Discharge Standard Coverage
              </h2>
              <p className="text-slate-400 max-w-2xl mx-auto">
                Built-in compliance rules for every major regulatory regime.
                Switch standards with one click — limits update instantly.
              </p>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { flag: '🇬🇧', code: 'UK EA', title: 'UK Environment Agency', desc: 'EPR consents, BAT-AEL, IED, river quality classification, seasonal ammonia limits.', color: '#38bdf8' },
              { flag: '🇦🇺', code: 'AU EPA', title: 'Australia — State EPAs', desc: 'NSW, QLD, WA, VIC discharge licences. ANZECC/ARMCANZ 2000. Trade waste agreements.', color: '#34d399' },
              { flag: '🇪🇺', code: 'EU UWWTD', title: 'European UWWTD / IED', desc: 'UWWTD 91/271/EWG, IED 2010/75/EU, BREF reference documents, BAT-AEL compliance.', color: '#a78bfa' },
              { flag: '🇺🇸', code: 'US EPA', title: 'US EPA / NPDES', desc: 'CWA NPDES permits, 40 CFR Parts 122/133/413/433, industrial pretreatment program.', color: '#f59e0b' },
            ].map((s, i) => (
              <Reveal key={s.code} delay={i * 0.1}>
                <div className="bg-slate-800/30 border border-slate-700/20 rounded-xl p-5 hover:border-opacity-50 transition-all"
                  style={{ borderColor: `${s.color}20` }}>
                  <div className="text-3xl mb-3">{s.flag}</div>
                  <div className="text-[10px] font-bold uppercase tracking-widest mb-1" style={{ color: s.color }}>{s.code}</div>
                  <h4 className="text-sm font-bold text-white mb-2">{s.title}</h4>
                  <p className="text-xs text-slate-500 leading-relaxed">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═════ EQUIPMENT SHOWCASE ═════ */}
      <section className="py-24 px-6" style={{ background: 'linear-gradient(180deg, #0a1628, #0f1f3a)' }}>
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <div className="text-center mb-14">
              <div className="w-16 h-1 bg-amber-500 rounded-full mx-auto mb-6" />
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                16 Equipment Spec Pages, Each Validated
              </h2>
              <p className="text-slate-400 max-w-2xl mx-auto">
                Every unit operation has its own physics engine, OEM validation,
                and P&amp;ID process flow diagram — all calculated live in your browser.
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 mb-8">
            <EquipmentCard icon={Waves}        name="DAF"              desc="Dissolved Air Flotation"     delay={0}    />
            <EquipmentCard icon={Factory}      name="Lamella"          desc="Inclined Plate Settler"      delay={0.03} />
            <EquipmentCard icon={FlaskConical} name="pH Correction"    desc="Acid / Base Neutralisation"  delay={0.06} />
            <EquipmentCard icon={Wind}         name="Aeration"         desc="Fine Bubble Diffusion"       delay={0.09} />
            <EquipmentCard icon={Filter}       name="Multimedia"       desc="MMF / GAC / Cloth Filter"    delay={0.12} />
            <EquipmentCard icon={Gauge}        name="EQ Tank"          desc="Flow Equalisation"           delay={0.15} />
            <EquipmentCard icon={Cog}          name="Secondary"        desc="Clarifier / SBR / MBBR"      delay={0.18} />
            <EquipmentCard icon={Timer}        name="Buffer Tank"      desc="Surge / Flow Balancing"      delay={0.21} />
            <EquipmentCard icon={Droplets}     name="Sludge Holding"   desc="Thickening &amp; Dewatering"     delay={0.24} />
            <EquipmentCard icon={Beaker}       name="Dosing"           desc="Chemical Feed Systems"       delay={0.27} />
            <EquipmentCard icon={Thermometer}  name="GAC Filter"       desc="Granular Activated Carbon"   delay={0.3}  />
            <EquipmentCard icon={Droplets}     name="Cascade Aerator"  desc="Open-Channel Re-aeration"    delay={0.33} />
            <EquipmentCard icon={FlaskConical} name="Ammonia Stripper" desc="Forced-Draft NH₃ Removal"    delay={0.36} />
            <EquipmentCard icon={Layers}       name="Packed Column"    desc="VOC / NH₃ Mass Transfer"     delay={0.39} />
            <EquipmentCard icon={Droplets}     name="Desalination DAF" desc="Seawater Pre-treatment"      delay={0.42} />
            <EquipmentCard icon={Recycle}      name="Process Train"    desc="Auto-built Treatment Trains" delay={0.45} />
            <EquipmentCard icon={Activity}     name="Pump Database"    desc="43 Models — EDUR/Nikuni/QED" delay={0.48} />
            <EquipmentCard icon={Wind}         name="Blower Selection" desc="Rotary / Centrifugal Sizing" delay={0.51} />
            <EquipmentCard icon={Calculator}   name="Pipe Flow Calc"   desc="Darcy-Weisbach Head Loss"    delay={0.54} />
            <EquipmentCard icon={List}         name="Equipment List"   desc="Registry &amp; Datasheets"       delay={0.57} />
          </div>

          {/* Validation badges */}
          <Reveal delay={0.4}>
            <div className="flex flex-wrap justify-center gap-3 mt-6">
              {[
                { label: 'HLR Validated', color: '#10b981' },
                { label: 'A/S Ratio Check', color: '#10b981' },
                { label: 'Retention Time', color: '#10b981' },
                { label: 'G-value (Camp-Stein)', color: '#f59e0b' },
                { label: 'Henry\'s Law Air Solubility', color: '#38bdf8' },
                { label: 'Stokes\' Rise Velocity', color: '#38bdf8' },
                { label: 'Affinity Law Pump Curves', color: '#a78bfa' },
                { label: 'Power Number Mixing', color: '#a78bfa' },
                { label: 'OEM Datasheet Cross-check', color: '#10b981' },
              ].map((b) => (
                <span key={b.label}
                  className="text-[10px] font-bold px-3 py-1 rounded-full border"
                  style={{ color: b.color, borderColor: `${b.color}30`, background: `${b.color}10` }}>
                  ✓ {b.label}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═════ RESERVOIR ENGINEERING SUITE ═════ */}
      <section className="py-24 px-6 relative overflow-hidden"
        style={{ background: 'linear-gradient(180deg, #0f1f3a, #0a1628)' }}>
        {/* Radial blue glow */}
        <div className="absolute inset-0 pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse at 30% 40%, rgba(56,189,248,0.08) 0%, transparent 60%), radial-gradient(ellipse at 70% 60%, rgba(96,165,250,0.06) 0%, transparent 50%)'
          }}
        />
        <div className="max-w-6xl mx-auto relative z-10">
          <Reveal>
            <div className="text-center mb-14">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-sky-500/30 text-sky-300 text-[10px] font-bold tracking-widest uppercase mb-4">
                <Waves className="w-3 h-3" />
                Flagship Module — Reservoir Project
              </div>
              <div className="w-16 h-1 bg-sky-500 rounded-full mx-auto mb-6" />
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Reservoir Engineering — 9 Phases · 36 Pages
              </h2>
              <p className="text-slate-400 max-w-2xl mx-auto">
                Complete workflow for raw-water reservoir aeration and remediation,
                modelled directly on the published <span className="text-sky-300 font-semibold">Barcombe Reservoir</span> design study —
                bathymetry capture through diffuser design, plume mapping, thermal stratification,
                algal kinetics, GHG balance, validation, and pilot-trial planning.
              </p>
            </div>
          </Reveal>

          {/* ─── Enhanced-options highlight band ─── */}
          <Reveal delay={0.05}>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-10">
              {[
                { icon: ShieldCheck, label: 'AWWA M37',        sub: 'Compliance toggle' },
                { icon: Database,    label: 'Auto-save',       sub: 'Per-page sessions' },
                { icon: Boxes,       label: '10 Modules',      sub: 'Tick to enable' },
                { icon: Upload,      label: 'Bathymetry CSV',  sub: 'Survey import' },
                { icon: FileText,    label: 'MASTER Report',   sub: '10-part dossier' },
                { icon: BookOpen,    label: '60+ Citations',   sub: 'Harvard export' },
              ].map((f) => (
                <div key={f.label}
                  className="flex items-center gap-2.5 rounded-xl border border-sky-500/15 bg-sky-500/5 px-3 py-2.5">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                    style={{ background: 'rgba(56,189,248,0.12)', border: '1px solid rgba(56,189,248,0.25)' }}>
                    <f.icon className="w-4 h-4 text-sky-300" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[11px] font-bold text-white truncate">{f.label}</div>
                    <div className="text-[10px] text-slate-400 truncate">{f.sub}</div>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              {
                phase: 'Phase 1 · Site &amp; Conditions',
                subtitle: 'Reservoir, catchment &amp; field data capture',
                color: '#38bdf8',
                items: [
                  { icon: Waves,         label: 'Reservoir Management', desc: 'Physical characteristics, catchment, residence time', path: '/reservoir-management' },
                  { icon: ClipboardList, label: 'Site Visit Form',      desc: 'Bathymetry, sediment, water chemistry, power, acoustics record', path: '/site-data-capture' },
                  { icon: Database,      label: 'Simulation Data Hub',  desc: 'Pre-selection datums — apply to override defaults app-wide', path: '/simulation-data' },
                  { icon: Upload,        label: 'Bathymetry Import',    desc: 'CSV upload — build contour-aware bed model', path: '/bathymetry-import' },
                ],
              },
              {
                phase: 'Phase 2 · Diffuser Design',
                subtitle: 'Bubble · SOTE · plume · fouling',
                color: '#22d3ee',
                items: [
                  { icon: Wind,       label: 'Reservoir Aeration Spec', desc: 'System sizing — air demand, AOTR, blower duty', path: '/reservoir-aeration-spec' },
                  { icon: Layers,     label: 'Diffuser Module',         desc: 'Single-module physics: pores, SOTE, bubble, AOTR', path: '/diffuser-module' },
                  { icon: Sparkles,   label: 'Custom Diffuser',         desc: 'Build custom geometry, propagate across the tool', path: '/custom-diffuser' },
                  { icon: Sparkles,   label: 'Custom Diffuser System',  desc: 'Multi-loop array — header, lateral, drop-leg', path: '/custom-diffuser-system' },
                  { icon: Grid3X3,    label: 'Diffuser Comparison',     desc: 'Side-by-side scoring of 6 candidate diffusers', path: '/diffuser-comparison' },
                  { icon: Wind,       label: 'Pore Release Geometry',   desc: 'Pore-axis angle, initial velocity vector, wind correction', path: '/pore-release-geometry' },
                  { icon: TrendingUp, label: 'Fouling Drift (10y)',     desc: '10-year membrane creep + SOTE drift with cleaning cycles', path: '/fouling-drift' },
                ],
              },
              {
                phase: 'Phase 3 · Module Array &amp; Layout',
                subtitle: 'Aggregate single modules to bed array',
                color: '#2dd4bf',
                items: [
                  { icon: Grid3X3, label: 'Module Array',     desc: 'Arrangement of multiple modules across reservoir zones', path: '/module-array' },
                  { icon: MapPin,  label: 'Reservoir Layout', desc: 'Full layout plan with array zones &amp; exclusion zones', path: '/reservoir-layout' },
                  { icon: MapPin,  label: 'Plume Coverage Map', desc: 'Array overlay on the reservoir; AWWA M37 compliance check', path: '/plume-coverage' },
                  { icon: TestTube,label: 'Sampling Plan',     desc: 'Predicted sample locations, per-visit input &amp; trend', path: '/sampling-plan' },
                ],
              },
              {
                phase: 'Phase 4 · Reservoir Physics',
                subtitle: 'Bed · thermal · algae · GHG',
                color: '#34d399',
                items: [
                  { icon: Mountain,     label: 'Sediment Bed Physics',   desc: 'Bed-type physics across silt/sand for multiple depths', path: '/sediment-bed-physics' },
                  { icon: FlaskConical, label: 'Bed Deep Assessment',    desc: '15-section deep physics with advanced bubble corrections (v47)', path: '/bed-assessment' },
                  { icon: Thermometer,  label: 'Thermal Model',          desc: 'Henderson-Sellers monthly heat budget, stratification, DO sat', path: '/thermal-model' },
                  { icon: FlaskConical, label: 'Algal Dynamics',         desc: '180-day trajectory: algae + heterotrophic bacteria + nitrifiers', path: '/algal-dynamics' },
                  { icon: Activity,     label: 'WQ Predictor (live)',    desc: '11-variable ecosystem trajectory with adjustable aeration', path: '/water-quality-predictor' },
                  { icon: Leaf,         label: 'GHG Balance',            desc: '20-year tCO₂e: methane, blower power, avoided treatment', path: '/ghg-balance' },
                ],
              },
              {
                phase: 'Phase 5 · Validation &amp; Sensitivity',
                subtitle: 'Cross-check every step against literature',
                color: '#a3e635',
                items: [
                  { icon: Workflow,       label: 'Calculation Chain',    desc: 'Pore → diffuser → module → array → reservoir → algae, every step cited', path: '/calculation-chain' },
                  { icon: ClipboardList,  label: 'Calculation Steps',    desc: 'Detailed step-by-step calculation report', path: '/reservoir-steps' },
                  { icon: ClipboardCheck, label: 'Validation Steps',     desc: 'Step-by-step PASS / WARN / FAIL across the design chain', path: '/validation-steps' },
                  { icon: ClipboardCheck, label: 'Reservoir Validation', desc: 'Validation against literature bands across every regime', path: '/reservoir-validation' },
                  { icon: ClipboardCheck, label: 'Diffuser Validation',  desc: 'Chosen-diffuser validation against literature', path: '/diffuser-validation' },
                  { icon: BarChart3,      label: 'Sensitivity Tornado',  desc: 'Saltelli OAT analysis — most-sensitive inputs', path: '/sensitivity' },
                ],
              },
              {
                phase: 'Phase 6 · Engineering &amp; Plant',
                subtitle: 'Blowers, acoustics, plant equipment',
                color: '#f59e0b',
                items: [
                  { icon: Wind,    label: 'Blower Selection', desc: 'Match seasonal demand against blower families with N+1 redundancy', path: '/blower-selection' },
                  { icon: Volume2, label: 'Acoustic Noise',   desc: 'BS 4142:2014 specific LAeq at noise-sensitive receptors', path: '/acoustic-noise' },
                ],
              },
              {
                phase: 'Phase 7 · Compliance · Risk · Pilot',
                subtitle: 'Regulatory gateway, risk register, pilot trial',
                color: '#fb923c',
                items: [
                  { icon: ShieldAlert,  label: 'Risk Register',        desc: 'Engineering, safety, environmental, commercial, regulatory risks', path: '/risk-register' },
                  { icon: ShieldCheck,  label: 'Reservoir Compliance', desc: 'Reservoirs Act, DWI, EA, CDM 2015, DSEAR, BS 4142, SEW sign-off', path: '/reservoir-compliance' },
                  { icon: FlaskConical, label: 'Pilot Trial Planner',  desc: '8-week side-by-side trial schedule, instruments, success criteria', path: '/pilot-trial-plan' },
                ],
              },
              {
                phase: 'Phase 8 · O&amp;M &amp; Lifecycle',
                subtitle: 'Operations, maintenance, long-term performance',
                color: '#f87171',
                items: [
                  { icon: Wrench, label: 'O&amp;M Schedule', desc: 'Year-by-year operations &amp; maintenance calendar with spares', path: '/om-schedule' },
                ],
              },
              {
                phase: 'Phase 9 · Reports &amp; Reference',
                subtitle: 'Deliverables, citations, master report',
                color: '#a78bfa',
                items: [
                  { icon: FileText, label: 'MASTER Report', desc: 'Consolidated 10-part design dossier — the headline deliverable', path: '/reservoir-master' },
                  { icon: FileText, label: 'PDF Index',     desc: 'Catalogue of every PDF the tool can generate', path: '/pdf-index' },
                  { icon: BookOpen, label: 'Citation Library', desc: '60+ literature references with Harvard-style export', path: '/citations' },
                ],
              },
            ].map((group, gi) => (
              <Reveal key={group.phase} delay={gi * 0.05}>
                <div className="bg-slate-900/50 border border-slate-800/30 rounded-xl overflow-hidden h-full hover:border-opacity-60 transition-all flex flex-col"
                  style={{ borderColor: `${group.color}25` }}>
                  <div className="px-4 py-3 border-b border-slate-800/30"
                    style={{ background: `${group.color}10` }}>
                    <div className="text-[10px] font-bold uppercase tracking-widest"
                      style={{ color: group.color }}
                      dangerouslySetInnerHTML={{ __html: group.phase }}
                    />
                    <div className="text-[10px] text-slate-500 mt-0.5"
                      dangerouslySetInnerHTML={{ __html: group.subtitle }}
                    />
                  </div>
                  <div className="p-3 space-y-2 flex-1">
                    {group.items.map((it) => (
                      <button
                        key={it.path}
                        onClick={() => navigate(it.path)}
                        className="w-full flex items-start gap-2.5 px-2.5 py-2 rounded-lg text-left text-xs text-slate-300 hover:bg-slate-800/60 hover:text-white transition-all group"
                      >
                        <div className="w-7 h-7 rounded-md flex items-center justify-center shrink-0 mt-0.5"
                          style={{ background: `${group.color}15`, border: `1px solid ${group.color}25` }}>
                          <it.icon className="w-3.5 h-3.5"
                            style={{ color: group.color }} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-xs font-semibold text-slate-200 group-hover:text-white flex items-center gap-1"
                            dangerouslySetInnerHTML={{ __html: it.label }}
                          />
                          <div className="text-[10px] text-slate-500 leading-snug mt-0.5"
                            dangerouslySetInnerHTML={{ __html: it.desc }}
                          />
                        </div>
                        <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-60 -translate-x-1 group-hover:translate-x-0 transition-all mt-1.5 shrink-0"
                          style={{ color: group.color }} />
                      </button>
                    ))}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          {/* ─── Enhanced options detail strip ─── */}
          <Reveal delay={0.3}>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="rb-feature-card" style={{ ['--rb-fc-accent' as string]: '#38bdf8' }}>
                <span className="rb-feature-card__line" aria-hidden style={{ background: 'linear-gradient(to right, transparent, #38bdf866, transparent)' }} />
                <div className="flex items-start gap-3">
                  <div className="rb-feature-card__icon" style={{ background: '#38bdf815', borderColor: '#38bdf830', color: '#38bdf8' }}>
                    <ShieldCheck className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-white mb-1">AWWA M37 Compliance</h3>
                    <p className="text-sm text-slate-400">
                      One toggle re-checks every diffuser placement, plume overlap and array footprint
                      against AWWA M37 (2017) reservoir-aeration guidance. Validation badges turn
                      compliant green or flag specifics.
                    </p>
                  </div>
                </div>
              </div>

              <div className="rb-feature-card" style={{ ['--rb-fc-accent' as string]: '#34d399' }}>
                <span className="rb-feature-card__line" aria-hidden style={{ background: 'linear-gradient(to right, transparent, #34d39966, transparent)' }} />
                <div className="flex items-start gap-3">
                  <div className="rb-feature-card__icon" style={{ background: '#34d39915', borderColor: '#34d39930', color: '#34d399' }}>
                    <Database className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-white mb-1">Session Auto-save &amp; Export</h3>
                    <p className="text-sm text-slate-400">
                      Every input on every workflow page is persisted to <code className="text-emerald-300">localStorage</code>
                      the moment it changes. Hub shows a green dot on every saved page; full session
                      exports to JSON for backup or peer review.
                    </p>
                  </div>
                </div>
              </div>

              <div className="rb-feature-card" style={{ ['--rb-fc-accent' as string]: '#22d3ee' }}>
                <span className="rb-feature-card__line" aria-hidden style={{ background: 'linear-gradient(to right, transparent, #22d3ee66, transparent)' }} />
                <div className="flex items-start gap-3">
                  <div className="rb-feature-card__icon" style={{ background: '#22d3ee15', borderColor: '#22d3ee30', color: '#22d3ee' }}>
                    <Boxes className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-white mb-1">Tick-of-the-Box Modules</h3>
                    <p className="text-sm text-slate-400">
                      Optional placement &amp; optimisation physics — Asaeda-Imberger, NSGA-II, GLM/ELCOM
                      coupling, Langmuir circulation. Each module ticks on across the relevant pages
                      with hard-conflict guarding and soft-warning surface.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.4}>
            <div className="text-center mt-10 flex flex-wrap justify-center gap-3">
              <button
                onClick={() => navigate('/reservoir-hub')}
                className="group inline-flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-sm bg-sky-500/10 border border-sky-500/40 text-sky-300 hover:bg-sky-500/20 hover:border-sky-400 transition-all"
              >
                <Waves className="w-4 h-4" />
                Open Reservoir Hub
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
              <button
                onClick={() => navigate('/reservoir-master')}
                className="group inline-flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-sm bg-violet-500/10 border border-violet-500/40 text-violet-300 hover:bg-violet-500/20 hover:border-violet-400 transition-all"
              >
                <FileText className="w-4 h-4" />
                MASTER Report
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
              <button
                onClick={() => navigate('/modules')}
                className="group inline-flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-sm bg-cyan-500/10 border border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/20 hover:border-cyan-400 transition-all"
              >
                <Boxes className="w-4 h-4" />
                Advanced Modules
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═════ ADVANCED SIMULATION MODULES ═════ */}
      <section className="py-24 px-6"
        style={{ background: 'linear-gradient(180deg, #0a1628, #020617)' }}>
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <div className="text-center mb-14">
              <div className="w-16 h-1 bg-cyan-500 rounded-full mx-auto mb-6" />
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                10 Advanced Simulation Modules
              </h2>
              <p className="text-slate-400 max-w-2xl mx-auto">
                Research-grade physics couplings — from Asaeda-Imberger destratification
                to NSGA-II multi-objective Pareto optimisation and GLM/ELCOM water-column models.
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: Activity,     name: 'Asaeda–Imberger C',   desc: 'Plume destratification efficiency coefficient', path: '/module/asaeda-imberger', color: '#38bdf8' },
              { icon: Mountain,     name: 'Deep-zone Weighting', desc: 'Bathymetric depth-weighted dosing', path: '/module/deep-zone-weighting', color: '#22d3ee' },
              { icon: Wind,         name: 'Wind-Tilt Correction',desc: 'Surface wind drag on plume trajectory', path: '/module/wind-tilt', color: '#34d399' },
              { icon: AlertOctagon, name: 'Resuspension Penalty',desc: 'Shear-induced sediment re-entrainment', path: '/module/resuspension-penalty', color: '#f59e0b' },
              { icon: PoundSterling,name: 'Cost per mg O₂',      desc: 'CAPEX + OPEX per kg oxygen delivered', path: '/module/cost-per-o2', color: '#facc15' },
              { icon: Sparkles,     name: 'DDS Auto-Optimiser',  desc: 'Diffuser density &amp; spacing search', path: '/module/dds-optimiser', color: '#a78bfa' },
              { icon: GitBranch,    name: 'NSGA-II Pareto',      desc: 'Multi-objective (cost · WQ · GHG) front', path: '/module/nsga2', color: '#f472b6' },
              { icon: Layers,       name: 'GLM Coupling',        desc: 'General Lake Model 1D water column', path: '/module/glm-coupling', color: '#06b6d4' },
              { icon: Box,          name: 'ELCOM Coupling',      desc: '3D hydrodynamic estuarine model link', path: '/module/elcom-coupling', color: '#818cf8' },
              { icon: Wind,         name: 'Langmuir Circulation',desc: 'Wind-driven helical surface cells', path: '/module/langmuir', color: '#0ea5e9' },
            ].map((m, i) => (
              <Reveal key={m.path} delay={i * 0.05}>
                <button
                  onClick={() => navigate(m.path)}
                  className="w-full text-left bg-slate-900/40 border border-slate-800/30 rounded-xl p-5 hover:bg-slate-900/70 hover:-translate-y-0.5 transition-all group"
                  style={{ borderColor: `${m.color}20` }}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                      style={{ background: `${m.color}15`, border: `1px solid ${m.color}30` }}>
                      <m.icon className="w-5 h-5" style={{ color: m.color }} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h4 className="text-sm font-bold text-white mb-1 flex items-center gap-2">
                        {m.name}
                        <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all" style={{ color: m.color }} />
                      </h4>
                      <p className="text-xs text-slate-500 leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: m.desc }}
                      />
                    </div>
                  </div>
                </button>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.4}>
            <div className="text-center mt-10">
              <button
                onClick={() => navigate('/modules')}
                className="group inline-flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-sm bg-cyan-500/10 border border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/20 hover:border-cyan-400 transition-all"
              >
                <Boxes className="w-4 h-4" />
                Open Modules Hub
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═════ PROCESS TRAIN FLOW ═════ */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <div className="text-center mb-14">
              <div className="w-16 h-1 bg-sky-500 rounded-full mx-auto mb-6" />
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                From Influent to Compliant Effluent
              </h2>
              <p className="text-slate-400 max-w-2xl mx-auto">
                The tool assembles and validates the treatment train sequence from influent characterisation data.
              </p>
            </div>
          </Reveal>

          <div className="flex flex-wrap justify-center gap-2 md:gap-0">
            {[
              { label: 'Screening', icon: Filter },
              { label: 'EQ Tank', icon: Gauge },
              { label: 'DAF', icon: Waves },
              { label: 'Biological', icon: FlaskConical },
              { label: 'Clarifier', icon: Droplets },
              { label: 'Filtration', icon: Factory },
              { label: 'Disinfection', icon: ShieldCheck },
              { label: 'Effluent', icon: CheckCircle2 },
            ].map((step, i) => (
              <ProcessStep key={step.label} label={step.label} icon={step.icon} delay={i * 0.08} />
            ))}
          </div>
        </div>
      </section>

      {/* ═════ PARAMETER REFERENCE ═════ */}
      <section className="py-24 px-6" style={{ background: 'linear-gradient(180deg, #0f1f3a, #020617)' }}>
        <div className="max-w-5xl mx-auto">
          <Reveal>
            <div className="text-center mb-12">
              <div className="w-16 h-1 bg-amber-500 rounded-full mx-auto mb-6" />
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Water Quality Parameters &amp; Design Basis
              </h2>
              <p className="text-slate-400 max-w-2xl mx-auto">
                Each of the 25+ parameters accepted by the tool feeds directly into the
                relevant physics models. The table below shows which calculations each
                measurement drives.
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {[
              {
                group: 'Organic Load',
                color: '#38bdf8',
                params: [
                  { name: 'BOD₅', unit: 'mg/L', drives: 'Biological oxygen demand — aerator sizing, SRT, MLSS target' },
                  { name: 'COD', unit: 'mg/L', drives: 'Chemical oxygen demand — biodegradability ratio (BOD:COD)' },
                  { name: 'TSS', unit: 'mg/L', drives: 'Suspended solids load — DAF SLR, clarifier flux, sludge volume' },
                  { name: 'O&G', unit: 'mg/L', drives: 'Fat, oil & grease — DAF hydraulic loading, coagulant demand' },
                ],
              },
              {
                group: 'Nutrients',
                color: '#34d399',
                params: [
                  { name: 'NH₃-N', unit: 'mg/L', drives: 'Free ammonia nitrogen — nitrification demand, aeration load' },
                  { name: 'Total Phosphorus', unit: 'mg/L', drives: 'Biological / chemical phosphorus removal sizing' },
                  { name: 'Total Nitrogen', unit: 'mg/L', drives: 'TN mass balance — anoxic zone sizing for denitrification' },
                  { name: 'TKN', unit: 'mg/L', drives: 'Kjeldahl nitrogen — nitrifier population estimation' },
                ],
              },
              {
                group: 'Physical Chemistry',
                color: 'var(--rb-brand)',
                params: [
                  { name: 'pH', unit: '—', drives: 'Coagulation efficiency, chemical dose, Langelier saturation index' },
                  { name: 'Temperature', unit: '°C', drives: 'van\'t Hoff correction — all biological and gas-transfer rates' },
                  { name: 'Conductivity', unit: 'µS/cm', drives: 'TDS estimation, salinity classification, membrane pre-screening' },
                  { name: 'Alkalinity', unit: 'mg/L CaCO₃', drives: 'Buffer capacity — acid/base neutralisation chemical demand' },
                ],
              },
              {
                group: 'Regulated Metals',
                color: '#f472b6',
                params: [
                  { name: 'Pb, Cr, Cd, Hg', unit: 'mg/L', drives: 'Priority substance consent limits (UK EA / AU EPA / EU IED)' },
                  { name: 'Zn, Cu, Ni', unit: 'mg/L', drives: 'EQS comparison — WFD AA-EQS or ANZECC DGV freshwater' },
                  { name: 'Fe, Mn, As', unit: 'mg/L', drives: 'Groundwater recharge limits, drinking-water proximity checks' },
                  { name: 'Metals (total)', unit: 'mg/L', drives: 'Sludge classification — inert / non-hazardous / hazardous' },
                ],
              },
            ].map((group, gi) => (
              <Reveal key={group.group} delay={gi * 0.1}>
                <div className="bg-slate-900/50 border border-slate-800/30 rounded-xl overflow-hidden">
                  <div className="px-5 py-3 border-b border-slate-800/30 flex items-center gap-2"
                    style={{ background: `${group.color}08` }}>
                    <Beaker className="w-4 h-4" style={{ color: group.color }} />
                    <span className="text-xs font-bold uppercase tracking-widest" style={{ color: group.color }}>
                      {group.group}
                    </span>
                  </div>
                  <div className="divide-y divide-slate-800/50">
                    {group.params.map((p) => (
                      <div key={p.name} className="px-5 py-3 flex items-start gap-3">
                        <div className="w-28 shrink-0">
                          <span className="text-xs font-bold text-white font-mono">{p.name}</span>
                          <span className="text-[10px] text-slate-600 block">{p.unit}</span>
                        </div>
                        <p className="text-xs text-slate-500 leading-relaxed">{p.drives}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═════ ENTERPRISE WORKFLOW ═════ */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <div className="text-center mb-14">
              <div className="w-16 h-1 bg-amber-500 rounded-full mx-auto mb-6" />
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Enterprise Workflow — End to End
              </h2>
              <p className="text-slate-400 max-w-2xl mx-auto">
                Covering the complete engineering and commercial workflow from
                initial sample entry to proposal generation.
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              {
                icon: FolderOpen, color: '#06b6d4',
                title: 'Customer & Site Management',
                bullets: [
                  'Add unlimited customers with contact details, country, and regulatory standard',
                  'Create multiple sites per customer — address, postcode, discharge route, permit reference',
                  'Attach influent samples to each site for full traceability',
                  'Searchable database across all customers and sites',
                ],
              },
              {
                icon: BookOpen, color: '#34d399',
                title: 'Sample Library',
                bullets: [
                  '25+ parameter influent samples — pH, BOD, COD, TSS, O&G, NH₃, TP, TN, metals',
                  'Auto-generated report numbers for audit trails',
                  'Link samples to process train and DAF calculations',
                  'Export and compare multiple samples side-by-side',
                ],
              },
              {
                icon: Cpu, color: 'var(--rb-brand)',
                title: 'Live Physics Calculations',
                bullets: [
                  'Every slider triggers instant recalculation of the full physics model',
                  'DAF: Henry\'s law, Stokes\' bubble rise, A/S ratio, G-value, power draw',
                  'Pump curves: affinity law interpolation across 43 OEM models',
                  'Chemical dosing: coagulant demand, pH correction, alkalinity buffer',
                ],
              },
              {
                icon: ClipboardList, color: '#f472b6',
                title: 'Proposals & Reports',
                bullets: [
                  'Auto-generate professional engineering proposals from design data',
                  'Country-traceable proposal numbers (AU-XXXX, UK-XXXX, US-XXXX, EU-XXXX)',
                  'Equipment summaries, process descriptions, and sizing tables',
                  'PDF export for client presentation and tender submission',
                ],
              },
              {
                icon: LayoutDashboard, color: '#a78bfa',
                title: 'Admin & Operations Dashboard',
                bullets: [
                  'Overview KPIs: assessments, samples, customers, and proposals at a glance',
                  'Activity log with full audit trail per user action',
                  'Admin tools: user management, data seeding, demo data provisioning',
                  'Equipment registry with OEM validation hub',
                ],
              },
              {
                icon: Globe, color: '#38bdf8',
                title: 'Multi-Country Compliance',
                bullets: [
                  'UK EA: EPR consents, IED BAT-AEL, seasonal limits, river quality',
                  'AU EPA: NSW, QLD, WA discharge licences, ANZECC/ARMCANZ 2000',
                  'EU UWWTD: 91/271/EWG, IED, BREF reference docs',
                  'US EPA: NPDES, 40 CFR 122/133/413/433, pretreatment standards',
                ],
              },
            ].map((card, i) => (
              <Reveal key={card.title} delay={i * 0.1}>
                <div className="bg-slate-800/20 border border-slate-700/20 rounded-xl p-6 hover:border-opacity-40 transition-all h-full"
                  style={{ borderColor: `${card.color}20` }}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ background: `${card.color}15`, border: `1px solid ${card.color}25` }}>
                      <card.icon className="w-5 h-5" style={{ color: card.color }} />
                    </div>
                    <h3 className="text-base font-bold text-white">{card.title}</h3>
                  </div>
                  <ul className="space-y-2">
                    {card.bullets.map((b, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-slate-400">
                        <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: card.color }} />
                        {b}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═════ SAMPLING &amp; COMPLIANCE WORKFLOW ═════ */}
      <section className="py-24 px-6"
        style={{ background: 'linear-gradient(180deg, #020617, #0a1628)' }}>
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <div className="text-center mb-14">
              <div className="w-16 h-1 bg-emerald-500 rounded-full mx-auto mb-6" />
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Sampling, Trends &amp; Compliance — Full Audit Loop
              </h2>
              <p className="text-slate-400 max-w-2xl mx-auto">
                Influent and effluent capture, study workbenches, trend analytics,
                discharge-permit comparison, and live compliance dashboards.
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              {
                color: '#38bdf8', icon: Droplets, title: 'Influent Capture',
                items: [
                  { label: 'Add Influent Sample',  path: '/influent-entry' },
                  { label: 'Influent Records',     path: '/influent' },
                  { label: 'Influent Trends',      path: '/influent-trends' },
                  { label: 'Influent Study',       path: '/influent-study' },
                ],
              },
              {
                color: '#34d399', icon: Beaker, title: 'Effluent &amp; Discharge',
                items: [
                  { label: 'Add Effluent Sample',  path: '/effluent-entry' },
                  { label: 'Discharge Records',    path: '/effluent' },
                  { label: 'Effluent Trends',      path: '/effluent-trends' },
                  { label: 'Effluent Reports',     path: '/effluent-reports' },
                  { label: 'Effluent Study',       path: '/effluent-study' },
                ],
              },
              {
                color: '#a78bfa', icon: ShieldCheck, title: 'Compliance &amp; Hub',
                items: [
                  { label: 'Compliance Gateway',   path: '/compliance-gateway' },
                  { label: 'Compliance Dashboard', path: '/compliance-dashboard' },
                  { label: 'Sample Workflow',      path: '/sample-workflow' },
                  { label: 'Customer Site Hub',    path: '/customer-site' },
                ],
              },
              {
                color: '#f472b6', icon: SlidersHorizontal, title: 'Assessment &amp; Dosing',
                items: [
                  { label: 'Site Assessment',         path: '/parameters' },
                  { label: 'Draft Influent Entry',    path: '/site-assessment' },
                  { label: 'Dosing',                  path: '/dosing' },
                  { label: 'Chemical Dosing',         path: '/chemical-dosing' },
                  { label: 'Impeller Mixing',         path: '/impeller-mixing' },
                  { label: 'Dosing &amp; Mixing Train',  path: '/dosing-mixing-train' },
                ],
              },
              {
                color: '#f59e0b', icon: FileText, title: 'Equipment &amp; Proposals',
                items: [
                  { label: 'Equipment List',         path: '/equipment-list' },
                  { label: 'Equipment Validation',   path: '/equipment-validation' },
                  { label: 'Process Train',          path: '/process-train' },
                  { label: 'Proposals',              path: '/proposals' },
                  { label: 'Process Database',       path: '/process-db' },
                ],
              },
              {
                color: '#06b6d4', icon: BarChart3, title: 'Data &amp; Reports',
                items: [
                  { label: 'Customers',          path: '/customers' },
                  { label: 'Samples',            path: '/samples' },
                  { label: 'Trends',             path: '/trends' },
                  { label: 'Reports',            path: '/reports' },
                  { label: 'Standards',          path: '/standards' },
                  { label: 'SA Water Charges',   path: '/sa-water-charges' },
                ],
              },
            ].map((g, gi) => (
              <Reveal key={g.title} delay={gi * 0.06}>
                <div className="bg-slate-900/40 border border-slate-800/30 rounded-xl overflow-hidden h-full"
                  style={{ borderColor: `${g.color}20` }}>
                  <div className="px-4 py-3 border-b border-slate-800/30 flex items-center gap-2"
                    style={{ background: `${g.color}10` }}>
                    <g.icon className="w-4 h-4" style={{ color: g.color }} />
                    <span className="text-xs font-bold tracking-wide"
                      style={{ color: g.color }}
                      dangerouslySetInnerHTML={{ __html: g.title }}
                    />
                  </div>
                  <div className="p-3 space-y-1">
                    {g.items.map((it) => (
                      <button
                        key={it.path}
                        onClick={() => navigate(it.path)}
                        className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-left text-xs text-slate-400 hover:text-white hover:bg-slate-800/60 transition-all group"
                      >
                        <ChevronRight className="w-3 h-3 shrink-0 transition-colors"
                          style={{ color: `${g.color}80` }} />
                        <span className="flex-1 truncate"
                          dangerouslySetInnerHTML={{ __html: it.label }}
                        />
                      </button>
                    ))}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═════ COMPLETE PLATFORM NAVIGATION (SITEMAP) ═════ */}
      <section className="py-24 px-6 relative overflow-hidden"
        style={{ background: 'linear-gradient(180deg, #0a1628, #0f1f3a)' }}>
        <div className="absolute inset-0 pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse at 50% 50%, rgba(205,164,46,0.05) 0%, transparent 60%)'
          }}
        />
        <div className="max-w-6xl mx-auto relative z-10">
          <Reveal>
            <div className="text-center mb-14">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-amber-500/30 text-amber-400 text-[10px] font-bold tracking-widest uppercase mb-4">
                <Globe className="w-3 h-3" />
                Every Page · Every Tool
              </div>
              <div className="w-16 h-1 bg-amber-500 rounded-full mx-auto mb-6" />
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Complete Platform Navigation
              </h2>
              <p className="text-slate-400 max-w-2xl mx-auto">
                100+ pages organised into nine functional groups. Click any tile to jump straight in.
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              {
                color: '#cda42e', icon: LayoutDashboard, title: 'Overview',
                items: [
                  { label: 'Dashboard',            path: '/' },
                  { label: 'Showcase',             path: '/advert' },
                  { label: 'Pipe Flow Calculator', path: '/pipe-flow-calculator' },
                  { label: 'Instructions',         path: '/instructions' },
                ],
              },
              {
                color: '#38bdf8', icon: Droplets, title: 'Water Sampling',
                items: [
                  { label: 'Add Influent Sample',  path: '/influent-entry' },
                  { label: 'Influent Records',     path: '/influent' },
                  { label: 'Influent Trends',      path: '/influent-trends' },
                  { label: 'Influent Study',       path: '/influent-study' },
                  { label: 'Add Effluent Sample',  path: '/effluent-entry' },
                  { label: 'Discharge Records',    path: '/effluent' },
                  { label: 'Effluent Trends',      path: '/effluent-trends' },
                  { label: 'Effluent Reports',     path: '/effluent-reports' },
                  { label: 'Effluent Study',       path: '/effluent-study' },
                  { label: 'Compliance Gateway',   path: '/compliance-gateway' },
                  { label: 'Sample Workflow',      path: '/sample-workflow' },
                  { label: 'Customer Site Hub',    path: '/customer-site' },
                  { label: 'Compliance Dashboard', path: '/compliance-dashboard' },
                ],
              },
              {
                color: '#34d399', icon: SlidersHorizontal, title: 'Assessment',
                items: [
                  { label: 'Site Assessment',      path: '/parameters' },
                  { label: 'Draft Influent Entry', path: '/site-assessment' },
                  { label: 'Dosing',               path: '/dosing' },
                ],
              },
              {
                color: '#0ea5e9', icon: Waves, title: 'Reservoir Project (★ Flagship)',
                items: [
                  { label: 'Reservoir Hub',         path: '/reservoir-hub' },
                  { label: 'Reservoir Management',  path: '/reservoir-management' },
                  { label: 'Site Visit Form',       path: '/site-data-capture' },
                  { label: 'Simulation Data Hub',   path: '/simulation-data' },
                  { label: 'Bathymetry Import',     path: '/bathymetry-import' },
                  { label: 'Reservoir Aeration',    path: '/reservoir-aeration-spec' },
                  { label: 'Diffuser Module',       path: '/diffuser-module' },
                  { label: 'Custom Diffuser',       path: '/custom-diffuser' },
                  { label: 'Custom Diffuser System',path: '/custom-diffuser-system' },
                  { label: 'Diffuser Comparison',   path: '/diffuser-comparison' },
                  { label: 'Pore Release Geometry', path: '/pore-release-geometry' },
                  { label: 'Fouling Drift (10y)',   path: '/fouling-drift' },
                  { label: 'Module Array',          path: '/module-array' },
                  { label: 'Reservoir Layout',      path: '/reservoir-layout' },
                  { label: 'Plume Coverage Map',    path: '/plume-coverage' },
                  { label: 'Sampling Plan',         path: '/sampling-plan' },
                  { label: 'Sediment Bed Physics',  path: '/sediment-bed-physics' },
                  { label: 'Bed Deep Assessment',   path: '/bed-assessment' },
                  { label: 'Thermal Model',         path: '/thermal-model' },
                  { label: 'Algal Dynamics',        path: '/algal-dynamics' },
                  { label: 'WQ Predictor',          path: '/water-quality-predictor' },
                  { label: 'GHG Balance',           path: '/ghg-balance' },
                  { label: 'Calculation Chain',     path: '/calculation-chain' },
                  { label: 'Calculation Steps',     path: '/reservoir-steps' },
                  { label: 'Validation Steps',      path: '/validation-steps' },
                  { label: 'Reservoir Validation',  path: '/reservoir-validation' },
                  { label: 'Diffuser Validation',   path: '/diffuser-validation' },
                  { label: 'Sensitivity Tornado',   path: '/sensitivity' },
                  { label: 'Blower Selection',      path: '/blower-selection' },
                  { label: 'Acoustic Noise',        path: '/acoustic-noise' },
                  { label: 'Risk Register',         path: '/risk-register' },
                  { label: 'Reservoir Compliance',  path: '/reservoir-compliance' },
                  { label: 'Pilot Trial Planner',   path: '/pilot-trial-plan' },
                  { label: 'O&amp;M Schedule',          path: '/om-schedule' },
                  { label: 'Master Report',         path: '/reservoir-master' },
                  { label: 'PDF Index',             path: '/pdf-index' },
                  { label: 'Citations',             path: '/citations' },
                ],
              },
              {
                color: '#22d3ee', icon: Boxes, title: 'Advanced Modules',
                items: [
                  { label: 'Modules Hub',          path: '/modules' },
                  { label: 'Asaeda-Imberger C',    path: '/module/asaeda-imberger' },
                  { label: 'Deep-zone Weighting',  path: '/module/deep-zone-weighting' },
                  { label: 'Wind-tilt Correction', path: '/module/wind-tilt' },
                  { label: 'Resuspension Penalty', path: '/module/resuspension-penalty' },
                  { label: 'Cost per mg O₂',       path: '/module/cost-per-o2' },
                  { label: 'DDS Auto-optimiser',   path: '/module/dds-optimiser' },
                  { label: 'NSGA-II Pareto',       path: '/module/nsga2' },
                  { label: 'GLM Coupling',         path: '/module/glm-coupling' },
                  { label: 'ELCOM Coupling',       path: '/module/elcom-coupling' },
                  { label: 'Langmuir Circulation', path: '/module/langmuir' },
                ],
              },
              {
                color: '#a78bfa', icon: FlaskConical, title: 'Research',
                items: [
                  { label: 'Chemical Dosing',       path: '/chemical-dosing' },
                  { label: 'Impeller Mixing',       path: '/impeller-mixing' },
                  { label: 'Dosing &amp; Mixing Train',path: '/dosing-mixing-train' },
                ],
              },
              {
                color: '#f59e0b', icon: Wrench, title: 'Equipment',
                items: [
                  { label: 'Equipment List',         path: '/equipment-list' },
                  { label: 'Equipment Validation',   path: '/equipment-validation' },
                  { label: 'Process Train',          path: '/process-train' },
                  { label: 'Proposals',              path: '/proposals' },
                  { label: 'Process Database',       path: '/process-db' },
                  { label: 'DAF Spec',               path: '/daf-spec' },
                  { label: 'Lamella Spec',           path: '/lamella-spec' },
                  { label: 'pH Spec',                path: '/ph-spec' },
                  { label: 'Aeration Spec',          path: '/aeration-spec' },
                  { label: 'Multimedia Spec',        path: '/multimedia-spec' },
                  { label: 'EQ Tank Spec',           path: '/eq-spec' },
                  { label: 'Secondary Spec',         path: '/secondary-spec' },
                  { label: 'Buffer Spec',            path: '/buffer-spec' },
                  { label: 'Sludge Holding',         path: '/sludge-spec' },
                  { label: 'GAC Spec',               path: '/gac-spec' },
                  { label: 'Cascade Aerator',        path: '/cascade-aerator-spec' },
                  { label: 'Ammonia Stripper',       path: '/ammonia-stripper-spec' },
                  { label: 'Packed Column Stripper', path: '/packed-column-stripper' },
                  { label: 'Desalination DAF',       path: '/desalination-daf-spec' },
                  { label: 'Pump Database',          path: '/pumps' },
                ],
              },
              {
                color: '#f472b6', icon: BarChart3, title: 'Data &amp; Reports',
                items: [
                  { label: 'Customers',        path: '/customers' },
                  { label: 'Samples',          path: '/samples' },
                  { label: 'Trends',           path: '/trends' },
                  { label: 'Reports',          path: '/reports' },
                  { label: 'Standards',        path: '/standards' },
                  { label: 'SA Water Charges', path: '/sa-water-charges' },
                  { label: 'Trend Reports',    path: '/trend-reports' },
                ],
              },
              {
                color: '#94a3b8', icon: KeyRound, title: 'Account',
                items: [
                  { label: 'My Profile',     path: '/profile' },
                  { label: 'Password Reset', path: '/password' },
                ],
              },
            ].map((g, gi) => (
              <Reveal key={g.title} delay={gi * 0.05}>
                <div className="bg-slate-900/40 border border-slate-800/30 rounded-xl overflow-hidden h-full"
                  style={{ borderColor: `${g.color}25` }}>
                  <div className="px-4 py-3 border-b border-slate-800/30 flex items-center gap-2"
                    style={{ background: `${g.color}10` }}>
                    <g.icon className="w-4 h-4" style={{ color: g.color }} />
                    <span className="text-xs font-bold tracking-wide uppercase"
                      style={{ color: g.color }}
                      dangerouslySetInnerHTML={{ __html: g.title }}
                    />
                    <span className="ml-auto text-[10px] font-mono text-slate-600">
                      {g.items.length}
                    </span>
                  </div>
                  <div className="p-2 grid grid-cols-2 gap-1">
                    {g.items.map((it) => (
                      <button
                        key={it.path}
                        onClick={() => navigate(it.path)}
                        className="flex items-center gap-1.5 px-2 py-1.5 rounded-md text-left text-[11px] text-slate-400 hover:text-white hover:bg-slate-800/60 transition-all"
                      >
                        <span className="w-1 h-1 rounded-full shrink-0"
                          style={{ background: `${g.color}aa` }} />
                        <span className="flex-1 truncate"
                          dangerouslySetInnerHTML={{ __html: it.label }}
                        />
                      </button>
                    ))}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═════ CTA ═════ */}
      <section className="py-24 px-6 relative overflow-hidden"
        style={{
          background: 'radial-gradient(ellipse at center, rgba(30,58,95,0.2) 0%, transparent 70%), linear-gradient(180deg, #020617, #0a1628)'
        }}>
        <div className="max-w-3xl mx-auto text-center relative z-10">
          <Reveal>
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
              Start Calculating
            </h2>
            <p className="text-lg text-slate-400 mb-10 max-w-xl mx-auto">
              No installation required. The full physics model runs in any modern web browser.
            </p>
          </Reveal>

          <Reveal delay={0.2}>
            <div className="flex flex-wrap gap-4 justify-center mb-12">
              <button
                onClick={() => navigate('/parameters')}
                className="group px-8 py-4 rounded-xl font-bold text-sm bg-gradient-to-r from-amber-500 to-amber-600 text-slate-200 shadow-xl shadow-amber-500/20 hover:shadow-amber-500/40 hover:-translate-y-0.5 transition-all flex items-center gap-2"
              >
                <Sparkles className="w-5 h-5" />
                Launch Smart Tool
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <button
                onClick={() => navigate('/daf-spec')}
                className="px-8 py-4 rounded-xl font-bold text-sm border border-slate-600 text-slate-300 hover:border-amber-500/40 hover:text-amber-400 transition-all"
              >
                Explore DAF Simulator
              </button>
            </div>
          </Reveal>

          <Reveal delay={0.4}>
            <div className="border-t border-slate-800/30 pt-8">
              <p className="text-sm text-slate-500 mb-1">Engineered by</p>
              <p className="text-xl font-bold text-amber-400">Reynolds &amp; Bauhm Smart Wastewater Tool</p>
              <p className="text-xs text-slate-600 mt-1">Smart Wastewater Assessment Tool v2.0</p>
              <div className="flex flex-wrap items-center justify-center gap-6 mt-6 text-slate-500">
                <span className="flex items-center gap-1.5 text-xs"><MapPin className="w-3.5 h-3.5" /> UK · Australia · EU · USA</span>
                <span className="flex items-center gap-1.5 text-xs"><Mail className="w-3.5 h-3.5" /> smarttool@reynoldsbauhm.com</span>
                <span className="flex items-center gap-1.5 text-xs"><Zap className="w-3.5 h-3.5 text-amber-400" /> v2.0 — Live Physics Engine</span>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═════ FOOTER ═════ */}
      <footer className="py-8 text-center border-t border-slate-800/50">
        <p className="text-xs text-slate-600">
          © {new Date().getFullYear()} Reynolds Bauhm. All physics models peer-reviewed.
          Equipment data sourced from OEM datasheets.
        </p>
      </footer>

      {/* Inline shimmer animation */}
      <style>{`
        @keyframes shimmer {
          0% { background-position: 200% center; }
          100% { background-position: -200% center; }
        }
      `}</style>
    </div>
  );
}
